/**
 * Fulfillment API — thin wrappers around backend endpoints.
 *
 * Uses getERPClient().call() for mode-aware API calls (JWT or session).
 * Company is resolved server-side from session — NOT passed from frontend.
 *
 * Plan: 123-pick-pack-fulfillment, Epic 01
 */

import { getERPClient } from "@/lib/erp/client";
import type {
  PersonalizationQueueParams,
  PaginatedPersonalizationQueue,
} from "./personalizationQueueTypes";

const API = "zaviago_backend.fulfillment.api";
const PICK_API = "zaviago_backend.fulfillment.pick_api";
const SORT_PACK_API = "zaviago_backend.fulfillment.sort_pack_api";
const SESSION_API = "zaviago_backend.fulfillment.session_api";
const PERSONALIZATION_API = "zaviago_backend.fulfillment.personalization_api";
const PARTIAL_API = "zaviago_backend.fulfillment.partial_shipment_api";
const DASHBOARD_API = "zaviago_backend.fulfillment.dashboard_api";
const PENDING_API = "zaviago_backend.fulfillment.pending_orders_api";
const LABEL_PRINT_API = "zaviago_backend.fulfillment.label_print_api";

export interface PendingOrder {
  name: string;
  customer_name: string;
  transaction_date: string | null;
  delivery_date: string | null;
  grand_total: number;
  item_count: number;
  ready_item_count: number;
  priority: "Normal" | "Rush" | "Express";
  has_personalized_items: boolean;
  has_pending_pwo: boolean;
  in_active_session: boolean;
  has_prior_shipments: boolean;
  fulfillment_blocked: boolean;
  /** Storefront the order came from (Sales Order custom_storefront_source). */
  custom_storefront_source?: string | null;
  /** Sales channel (Sales Order source_channel): Shopee, Lazada, TikTok Shop, Storefront, Desk. */
  source_channel?: string | null;
  /** Plan 1954 — awaiting-shipment queue: latest non-cancelled DN (or null). */
  delivery_note?: string | null;
  /** Plan 1954 — current tracking number (Delivery Note.lr_no), or null. */
  tracking_no?: string | null;
  /** Plan 1954 — true when any DN for this order already has a tracking number. */
  has_tracking?: boolean;
  /** Plan 1954 — "last packed/ready": latest completed-PWO time, else order date. */
  ready_at?: string | null;
}

export interface RecordShipmentResult {
  success: boolean;
  delivery_note: string;
  created_dn: boolean;
}

export interface BatchSuggestion {
  label: string;
  order_count: number;
  item_count: number;
  estimated_locations: number;
  sku_overlap_pct: number;
  shared_items: { name: string; image: string | null }[];
  has_rush: boolean;
  has_personalized: boolean;
  has_partial_orders: boolean;
  orders: string[];
}

export interface CreateSessionResult {
  session_name: string;
  pick_list_name: string;
  total_pick_steps: number;
  total_orders: number;
  /** Orders excluded from the session because ALL their pick rows were
   * dropped (out of stock / unresolved bundle variants) — TKT-2026-00133. */
  skipped_orders?: string[];
}

export interface FulfillmentCounts {
  sessions: number;
  pending_orders: number;
  parked_personalization: number;
}

export interface ParkedOrderPWO {
  pwo_name: string;
  status: string;
  item_code: string;
  item_name: string;
}

export interface ParkedOrder {
  name: string;
  customer_name: string;
  grand_total: number;
  transaction_date: string | null;
  delivery_date: string | null;
  pwo_count: number;
  pwos: ParkedOrderPWO[];
}

export interface PaginatedOrdersResponse {
  orders: PendingOrder[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface PendingOrdersParams {
  page?: number;
  page_size?: number;
  search?: string;
  priority_filter?: string;
  readiness_filter?: string;
  channel_filter?: string;
  /** JSON list / CSV of storefront keys to restrict to (store filter). */
  storefront_sources?: string;
  /** Plan 1954 — ISO date; only orders ready/packed on/after this date. */
  date_from?: string;
}

// ── Epic 02: Pick Walk types ──────────────────────────────

export interface PickStep {
  name: string;
  item_code: string;
  item_name: string;
  image: string | null;
  warehouse: string;
  qty: number;
  picked_qty: number;
  pick_status: "Pending" | "Confirmed" | "Short" | "Damaged";
  short_pick_qty: number;
  short_pick_reason: string | null;
  damage_photo: string | null;
  damage_notes: string | null;
  uom: string;
  sales_order: string;
  /** Storefront the order came from (Sales Order custom_storefront_source). */
  custom_storefront_source?: string | null;
  has_barcode: boolean;
  has_pending_pwo: boolean;
  order_breakdown: { sales_order: string; qty: number }[];
  // Present only when get_pick_steps is called with expand_sets=1 (review list):
  // a bundle bought in qty N is split into N "set" blocks so a packer can pick +
  // QC one full set at a time. set_total>1 marks an expanded set row.
  set_no?: number;
  set_total?: number;
  bundle_line?: string | null;
}

export interface PickSession {
  name: string;
  status: string;
  current_pick_step: number;
  total_pick_steps: number;
  total_orders: number;
  assigned_to: string;
}

export interface PickStepsResponse {
  session: PickSession;
  steps: PickStep[];
}

export interface UpdatePickStepResult {
  success: boolean;
  all_complete: boolean;
  next_step: number;
}

// ── Epic 03: Sort + Pack types ──────────────────────────

export interface SortInstruction {
  item_code: string;
  item_name: string;
  image: string | null;
  qty: number;
  sales_order: string;
  customer_name: string;
  /** Storefront the order came from (Sales Order custom_storefront_source). */
  custom_storefront_source?: string | null;
  // True when this garment needs personalization (PWO/spec rows). Set by backend
  // get_sort_instructions; the SVC-PERSONALIZATION addon row is no longer a
  // separate item — the parent garment carries this flag. Optional for backcompat.
  is_personalized?: boolean;
}

export interface SortInstructionsResponse {
  session: PickSession;
  instructions: SortInstruction[];
  pack_items: SortInstruction[];
  custom_items: SortInstruction[];
}

export interface PackOrderItem {
  item_code: string;
  item_name: string;
  image: string | null;
  qty: number;
  uom: string;
  row_name: string;
}

export interface PersonalizationSpec {
  parent_item_row: string;
  field_label: string;
  field_type: string;
  value: string;
  file_url: string | null;
}

export interface PackOrder {
  sales_order: string;
  customer_name: string;
  /** Storefront the order came from (Sales Order custom_storefront_source). */
  custom_storefront_source?: string | null;
  /** Sales channel (Sales Order source_channel): Shopee, Lazada, TikTok Shop, Storefront, Desk. */
  source_channel?: string | null;
  items: PackOrderItem[];
  pack_status: string;
  pack_photo: string | null;
  personalization: PersonalizationSpec[];
  delivery_note: string | null;
  packing_slip: string | null;
  tracking_no: string | null;
  pending_pwo_count: number;
  /** Shopee marketplace fields (plan 1958 F1) — null unless the order is Shopee. */
  shopee_order_status?: string | null;
  shopee_order_sn?: string | null;
  shopee_tracking_no?: string | null;
}

export interface PackOrdersResponse {
  session: PickSession;
  orders: PackOrder[];
}

export interface CreateDeliveryResult {
  delivery_note: string;
  packing_slip: string;
}

export interface PrintUrlResult {
  delivery_note_url: string;
  packing_slip_url: string | null;
  shipping_label_url: string;
}

export interface EnsureLabelResult {
  delivery_note: string;
  shipping_label_url: string;
  created: boolean;
}

export interface SOPrintUrlResult {
  packing_slip_url: string;
}

export interface SetTrackingResult {
  success: boolean;
}

// ── Batch Complete summary ──────────────────────────────

export interface SessionSummaryOrder {
  sales_order: string;
  customer_name: string;
  item_count: number;
  delivery_note: string | null;
  packing_slip: string | null;
  pack_status: string;
  packed_at: string | null;
  grand_total: number;
  currency: string;
}

export interface SessionSummaryResponse {
  session: PickSession;
  orders: SessionSummaryOrder[];
  total_items: number;
  total_orders: number;
  packed_orders: number;
  started_at: string | null;
  completed_at: string | null;
}

// ── Epic 04: Session Management types ──────────────────

export interface OrderReadiness {
  sales_order: string;
  ready_count: number;
  total_count: number;
  all_ready: boolean;
  pwo_types: string[];
}

export interface ActiveSession {
  name: string;
  status: string;
  assigned_to: string;
  assigned_to_name: string;
  priority: "Normal" | "Rush" | "Express";
  current_pick_step: number;
  current_pack_step: number;
  total_pick_steps: number;
  total_orders: number;
  started_at: string | null;
  paused_at: string | null;
  orders: string[];
  order_readiness: OrderReadiness[];
  is_auto_paused: boolean;
  /** Distinct storefront keys across this session's orders (store badges/filter). */
  storefront_sources?: string[];
  /** Plan 1958 F2 — SO names in this session that are Shopee orders in
   * READY_TO_SHIP status, i.e. eligible for one-tap batch shipping. Empty on
   * tenants without the Shopee custom fields. */
  shopee_ready_orders?: string[];
}

export interface CompletedSession {
  name: string;
  status: string;
  assigned_to: string;
  assigned_to_name: string;
  total_pick_steps: number;
  total_orders: number;
  total_qty: number;
  started_at: string | null;
  completed_at: string | null;
  orders: string[];
  /** Distinct storefront keys across this session's orders (store badges/filter). */
  storefront_sources?: string[];
  /** Plan 1958 F2 — SO names in this packed session that are Shopee orders still
   * in READY_TO_SHIP (packed but not yet shipped via Shopee's API). */
  shopee_ready_orders?: string[];
}

export interface PauseSessionResult {
  success: boolean;
  status: string;
}

export interface ResumeSessionResult {
  success: boolean;
  status: string;
  session: {
    name: string;
    status: string;
    current_pick_step: number;
    current_pack_step: number;
    total_pick_steps: number;
    total_orders: number;
    assigned_to: string;
  };
}

export const fulfillmentApi = {
  getFulfillmentCounts() {
    const client = getERPClient();
    return client.call<FulfillmentCounts>(`${API}.get_fulfillment_counts`, {});
  },

  getParkedPersonalizationOrders() {
    const client = getERPClient();
    return client.call<ParkedOrder[]>(
      `${PENDING_API}.get_parked_personalization_orders`,
      {},
    );
  },

  getPendingOrdersPaginated(params: PendingOrdersParams) {
    const client = getERPClient();
    return client.call<PaginatedOrdersResponse>(
      `${PENDING_API}.get_pending_orders_paginated`,
      params,
    );
  },

  getPendingOrders(storefrontSources?: string) {
    const client = getERPClient();
    return client.call<PendingOrder[]>(`${API}.get_pending_orders`, {
      ...(storefrontSources && { storefront_sources: storefrontSources }),
    });
  },

  suggestBatches(maxBatchSize = 10) {
    const client = getERPClient();
    return client.call<BatchSuggestion[]>(`${API}.suggest_batches`, {
      max_batch_size: maxBatchSize,
    });
  },

  createSession(salesOrders: string[], readyItemsOnly = false) {
    const client = getERPClient();
    return client.call<CreateSessionResult>(`${API}.create_fulfillment_session`, {
      sales_orders: JSON.stringify(salesOrders),
      ...(readyItemsOnly && { ready_items_only: 1 }),
    });
  },

  // ── Epic 02: Pick Walk ──────────────────────────────────

  getPickSteps(sessionName: string, expandSets = false) {
    const client = getERPClient();
    return client.call<PickStepsResponse>(`${PICK_API}.get_pick_steps`, {
      session_name: sessionName,
      expand_sets: expandSets ? 1 : 0,
    });
  },

  updatePickStep(
    sessionName: string,
    pickListItem: string,
    action: "confirm" | "short" | "damage",
    params?: { qty_picked?: number; reason?: string; damage_notes?: string },
  ) {
    const client = getERPClient();
    return client.call<UpdatePickStepResult>(`${PICK_API}.update_pick_step`, {
      session_name: sessionName,
      pick_list_item: pickListItem,
      action,
      ...params,
    });
  },

  async uploadDamagePhoto(
    sessionName: string,
    pickListItem: string,
    file: File,
  ): Promise<{ file_url: string }> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("session_name", sessionName);
    fd.append("pick_list_item", pickListItem);

    const client = getERPClient();
    return client.callWithFormData<{ file_url: string }>(
      `${PICK_API}.upload_damage_photo`,
      fd,
    );
  },

  verifyBarcode(itemCode: string, scannedBarcode: string) {
    const client = getERPClient();
    return client.call<{ match: boolean; expected_barcodes: string[] }>(
      `${PICK_API}.verify_barcode`,
      { item_code: itemCode, scanned_barcode: scannedBarcode },
    );
  },

  // ── Epic 03: Sort + Pack ──────────────────────────────────

  getSortInstructions(sessionName: string) {
    const client = getERPClient();
    return client.call<SortInstructionsResponse>(
      `${SORT_PACK_API}.get_sort_instructions`,
      { session_name: sessionName },
    );
  },

  completeSort(sessionName: string) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${SORT_PACK_API}.complete_sort`,
      { session_name: sessionName },
    );
  },

  getPackOrders(sessionName: string) {
    const client = getERPClient();
    return client.call<PackOrdersResponse>(
      `${SORT_PACK_API}.get_pack_orders`,
      { session_name: sessionName },
    );
  },

  getSessionSummary(sessionName: string) {
    const client = getERPClient();
    return client.call<SessionSummaryResponse>(
      `${SORT_PACK_API}.get_session_summary`,
      { session_name: sessionName },
    );
  },

  createDeliveryAndPack(sessionName: string, salesOrder: string) {
    const client = getERPClient();
    return client.call<CreateDeliveryResult>(
      `${SORT_PACK_API}.create_delivery_and_pack`,
      { session_name: sessionName, sales_order: salesOrder },
    );
  },

  async uploadPackPhoto(
    sessionName: string,
    salesOrder: string,
    file: File,
  ): Promise<{ file_url: string }> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("session_name", sessionName);
    fd.append("sales_order", salesOrder);

    const client = getERPClient();
    return client.callWithFormData<{ file_url: string }>(
      `${SORT_PACK_API}.upload_pack_photo`,
      fd,
    );
  },

  completePackOrder(sessionName: string, salesOrder: string) {
    const client = getERPClient();
    return client.call<{ success: boolean; all_complete: boolean }>(
      `${SORT_PACK_API}.complete_pack_order`,
      { session_name: sessionName, sales_order: salesOrder },
    );
  },

  completeSession(sessionName: string) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${SORT_PACK_API}.complete_session`,
      { session_name: sessionName },
    );
  },

  getPrintUrl(deliveryNote: string) {
    const client = getERPClient();
    return client.call<PrintUrlResult>(
      `${SORT_PACK_API}.get_print_url`,
      { delivery_note: deliveryNote },
    );
  },

  getSOPrintUrl(salesOrder: string) {
    const client = getERPClient();
    return client.call<SOPrintUrlResult>(
      `${SORT_PACK_API}.get_so_print_url`,
      { sales_order: salesOrder },
    );
  },

  ensureDeliveryLabel(salesOrder: string) {
    const client = getERPClient();
    return client.call<EnsureLabelResult>(
      `${LABEL_PRINT_API}.ensure_delivery_note_and_label`,
      { sales_order: salesOrder },
    );
  },

  setTrackingNumber(deliveryNote: string, trackingNo: string, transporterName?: string) {
    const client = getERPClient();
    return client.call<SetTrackingResult>(
      `${SORT_PACK_API}.set_tracking_number`,
      {
        delivery_note: deliveryNote,
        tracking_no: trackingNo,
        ...(transporterName && { transporter_name: transporterName }),
      },
    );
  },

  // Plan 1954 — record a shipment for an order shipped OUTSIDE a session.
  // Creates a draft DN if none exists (Path B, no stock posting), sets tracking,
  // and fires the customer LINE unless notify=false (retroactive backfill).
  recordShipment(
    salesOrder: string,
    trackingNo: string,
    transporterName?: string,
    notify: boolean = true,
  ) {
    const client = getERPClient();
    return client.call<RecordShipmentResult>(
      "zaviago_backend.fulfillment.shipment_entry_api.record_shipment",
      {
        sales_order: salesOrder,
        tracking_no: trackingNo,
        ...(transporterName && { transporter_name: transporterName }),
        notify: notify ? 1 : 0,
      },
    );
  },

  // ── Epic 04: Session Management ──────────────────────────

  pauseSession(sessionName: string, currentStep?: number) {
    const client = getERPClient();
    return client.call<PauseSessionResult>(`${SESSION_API}.pause_session`, {
      session_name: sessionName,
      ...(currentStep !== undefined && { current_step: currentStep }),
    });
  },

  resumeSession(sessionName: string) {
    const client = getERPClient();
    return client.call<ResumeSessionResult>(`${SESSION_API}.resume_session`, {
      session_name: sessionName,
    });
  },

  cancelSession(sessionName: string) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(`${SESSION_API}.cancel_session`, {
      session_name: sessionName,
    });
  },

  getActiveSessions() {
    const client = getERPClient();
    return client.call<ActiveSession[]>(`${SESSION_API}.get_active_sessions`, {});
  },

  getCompletedSessions() {
    const client = getERPClient();
    return client.call<CompletedSession[]>(`${SESSION_API}.get_completed_sessions`, {});
  },

  // ── Epic 05: Personalization Work Orders ──────────────────

  getPersonalizationQueue(params: PersonalizationQueueParams = {}) {
    const client = getERPClient();
    return client.call<PaginatedPersonalizationQueue>(
      `${PERSONALIZATION_API}.get_personalization_queue`,
      {
        ...(params.page !== undefined && { page: params.page }),
        ...(params.page_size !== undefined && { page_size: params.page_size }),
        ...(params.search && { search: params.search }),
        ...(params.assignedTo && { assigned_to: params.assignedTo }),
      },
    );
  },

  getPWODetail(pwoName: string) {
    const client = getERPClient();
    return client.call<PWODetailResponse>(
      `${PERSONALIZATION_API}.get_pwo_detail`,
      { pwo_name: pwoName },
    );
  },

  updatePWOStatus(pwoName: string, newStatus: string, notes?: string) {
    const client = getERPClient();
    return client.call<{ success: boolean; status: string }>(
      `${PERSONALIZATION_API}.update_pwo_status`,
      { pwo_name: pwoName, new_status: newStatus, ...(notes && { notes }) },
    );
  },

  async uploadWorkPhoto(
    pwoName: string,
    file: File,
  ): Promise<{ file_url: string }> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("pwo_name", pwoName);

    const client = getERPClient();
    return client.callWithFormData<{ file_url: string }>(
      `${PERSONALIZATION_API}.upload_work_photo`,
      fd,
    );
  },

  // ── Epic 06: Partial Shipments + Mixed Orders ──────────────

  getOrderReadiness(salesOrder: string) {
    const client = getERPClient();
    return client.call<OrderReadiness>(
      `${PARTIAL_API}.get_order_readiness`,
      { sales_order: salesOrder },
    );
  },

  getShipmentHistory(salesOrder: string) {
    const client = getERPClient();
    return client.call<ShipmentHistory>(
      `${PARTIAL_API}.get_shipment_history`,
      { sales_order: salesOrder },
    );
  },

  getPartialShipmentSettings() {
    const client = getERPClient();
    return client.call<PartialShipmentSettings>(
      `${PARTIAL_API}.get_partial_shipment_settings`,
      {},
    );
  },

  updatePartialShipmentSettings(settings: Partial<PartialShipmentSettings>) {
    const client = getERPClient();
    return client.call<{ success: boolean }>(
      `${PARTIAL_API}.update_partial_shipment_settings`,
      {
        ...(settings.allow_partial_shipments !== undefined && {
          allow_partial_shipments: settings.allow_partial_shipments ? 1 : 0,
        }),
        ...(settings.partial_shipment_policy && {
          partial_shipment_policy: settings.partial_shipment_policy,
        }),
        ...(settings.personalization_enabled !== undefined && {
          personalization_enabled: settings.personalization_enabled ? 1 : 0,
        }),
      },
    );
  },

  // ── Epic 07: Manager Dashboard ──────────────────────────────

  getDashboardMetrics(dateRange = "today") {
    const client = getERPClient();
    return client.call<FulfillmentMetrics>(
      `${DASHBOARD_API}.get_fulfillment_metrics`,
      { date_range: dateRange },
    );
  },

  getWorkerPerformance(dateRange = "today") {
    const client = getERPClient();
    return client.call<WorkerPerformance[]>(
      `${DASHBOARD_API}.get_worker_performance`,
      { date_range: dateRange },
    );
  },

  getFulfillmentAlerts() {
    const client = getERPClient();
    return client.call<FulfillmentAlerts>(
      `${DASHBOARD_API}.get_fulfillment_alerts`,
      {},
    );
  },
};

// ── Epic 05: Personalization Work Order types ──────────────

export interface PWOSpec_Inline {
  label: string;
  value: string;
  type: string;
}

export interface PWOCard {
  name: string;
  item_code: string;
  item_name: string;
  item_image: string | null;
  quantity: number;
  /** Garment colour resolved from bundle variant selections (QC display). */
  color: string;
  /** Garment size resolved from bundle variant selections (QC display). */
  size: string;
  customization_summary: string;
  specs: PWOSpec_Inline[];
  status: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  priority: string;
  estimated_completion: string | null;
  sales_order: string;
  channel: string;
  customer_name: string;
  work_photo: string | null;
  qa_notes: string | null;
  rejection_reason: string | null;
  created: string;
}

export interface PersonalizationQueue {
  queued: PWOCard[];
  in_progress: PWOCard[];
  qc: PWOCard[];
  complete: PWOCard[];
}

export interface PWOSpec {
  field_label: string;
  field_type: string;
  field_value: string;
  file_url: string | null;
}

export interface PWODetail extends PWOCard {
  customization_template: string | null;
  started_at: string | null;
  completed_at: string | null;
  qa_by: string | null;
  qa_at: string | null;
  output_warehouse: string | null;
  item_image: string | null;
}

export interface PWODetailResponse {
  pwo: PWODetail;
  specs: PWOSpec[];
}

// ── Epic 06: Partial Shipments + Mixed Orders types ──────────

export interface OrderReadinessItem {
  row_name: string;
  item_code: string;
  item_name: string;
  image: string | null;
  qty: number;
  status: "ready" | "pending";
  has_customization: boolean;
  pwo_name: string | null;
  pwo_status: string | null;
  estimated_completion: string | null;
}

export interface OrderReadiness {
  items: OrderReadinessItem[];
  ready_count: number;
  total_count: number;
  all_ready: boolean;
}

export interface ShipmentHistoryItem {
  item_code: string;
  item_name: string;
  qty: number;
}

export interface ShipmentHistoryEntry {
  delivery_note: string;
  posting_date: string | null;
  status: string;
  docstatus: number;
  items: ShipmentHistoryItem[];
  item_count: number;
  pack_photo: string | null;
}

export interface ShipmentHistory {
  shipments: ShipmentHistoryEntry[];
}

export interface PartialShipmentSettings {
  allow_partial_shipments: boolean;
  partial_shipment_policy: "Wait Until All Ready" | "Ship Ready Items Immediately";
  personalization_enabled?: boolean;
}

// ── Epic 07: Manager Dashboard types ──────────────────────────

export interface FulfillmentMetrics {
  orders_fulfilled: number;
  avg_pick_time_minutes: number;
  avg_pack_time_minutes: number;
  error_rate: number;
  pipeline: {
    pending: number;
    picking: number;
    sorting: number;
    packing: number;
    shipped: number;
    personalization: number;
  };
}

export interface WorkerPerformance {
  user: string;
  full_name: string;
  picks_count: number;
  picks_per_hour: number;
  orders_packed: number;
  short_picks: number;
  damages: number;
}

export interface FulfillmentAlert {
  name?: string;
  item_name?: string;
  item_code?: string;
  sales_order?: string;
  estimated_completion?: string;
  assigned_to_name?: string;
  status?: string;
  paused_at?: string;
  short_count?: number;
  priority?: string;
}

export interface FulfillmentAlerts {
  overdue_pwos: FulfillmentAlert[];
  stale_sessions: FulfillmentAlert[];
  high_error_items: FulfillmentAlert[];
}
